import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { CAREER_ROLES, CAREER_PROJECTS, CATEGORY_LABELS } from "@/data/content";

export default function Experience() {
  const { lang, t } = useLanguage();
  const e = t.experience;
  const [activeId, setActiveId] = useState(CAREER_ROLES[0].id);
  const activeRole = CAREER_ROLES.find((r) => r.id === activeId) || CAREER_ROLES[0];

  const roleProjects = useMemo(
    () => CAREER_PROJECTS.filter((p) => p.role === activeRole.id),
    [activeRole.id]
  );

  const skills = useMemo(() => {
    const seen = new Set();
    const ordered = [];
    roleProjects.forEach((p) => p.categories.forEach((c) => { if (!seen.has(c)) { seen.add(c); ordered.push(c); } }));
    return ordered;
  }, [roleProjects]);

  return (
    <section id="experience" className="py-24 md:py-32 lg:py-40 border-b border-line bg-surface">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-auroraDeep mb-6">{e.label}</p>
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl max-w-3xl leading-[1.05]">
            {e.titlePrefix}<span className="font-serif-accent italic font-medium">{e.titleHighlight}</span>
          </h2>
          <p className="mt-6 max-w-2xl text-lg font-light text-stone leading-relaxed">
            {e.subtitle}
          </p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-[320px_1fr] gap-10 lg:gap-16">
          {/* Vertical timeline */}
          <Reveal>
            <div className="relative lg:sticky lg:top-28 lg:self-start">
              <div className="relative pl-8">
                <div className="absolute left-[3px] top-3 bottom-3 w-px bg-line" aria-hidden="true" />
                <ul className="space-y-1">
                  {CAREER_ROLES.map((r, i) => {
                    const isActive = r.id === activeRole.id;
                    const n = String(i + 1).padStart(2, "0");
                    return (
                      <li key={r.id} className="relative">
                        <span
                          className={`absolute -left-8 top-[1.4rem] w-[7px] h-[7px] rounded-full transition-colors duration-500 ${isActive ? "bg-auroraDeep" : "bg-line"}`}
                          aria-hidden="true"
                        />
                        <button
                          onClick={() => setActiveId(r.id)}
                          className={`group w-full text-left py-4 px-4 transition-colors duration-300 ${isActive ? "bg-ink text-paper" : "hover:bg-paper"}`}
                          data-testid={`timeline-role-${r.id}`}
                        >
                          <span className={`font-mono-tech text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${isActive ? "text-aurora" : "text-auroraDeep"}`}>
                            {n} · {r.start} – {r.end === "present" ? e.presentLabel : r.end}
                          </span>
                          <p className={`font-display font-semibold text-lg leading-snug mt-1 transition-colors duration-300 ${!isActive ? "group-hover:text-auroraDeep" : ""}`}>
                            {r.company}
                          </p>
                          <p className={`font-mono-tech text-[11px] mt-0.5 ${isActive ? "text-paper/70" : "text-stone"}`}>{r.title}</p>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </Reveal>

          {/* Role detail + projects */}
          <div className="min-w-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeRole.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="border-b border-line pb-8">
                  <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-auroraDeep">{activeRole.industry[lang]}</p>
                  <h3 className="font-display font-bold text-3xl md:text-4xl mt-2">{activeRole.title}</h3>
                  <p className="font-serif-accent italic text-xl md:text-2xl text-stone mt-1">{activeRole.company}</p>
                  <p className="text-lg font-light text-stone leading-relaxed mt-5 max-w-3xl">
                    {activeRole.description[lang]}
                  </p>

                  {skills.length > 0 && (
                    <>
                      <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-stone mt-8 mb-3">{e.skillsLabel}</p>
                      <div className="flex flex-wrap gap-2">
                        {skills.map((c) => (
                          <span
                            key={c}
                            className="font-mono-tech text-[11px] uppercase tracking-wider border border-line text-stone px-3 py-1.5 transition-colors duration-300 hover:border-ink hover:text-ink"
                          >
                            {CATEGORY_LABELS[lang][c]}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {roleProjects.length > 0 && (
                  <>
                    <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-stone mt-10 mb-4">{e.keyProjectsLabel}</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {roleProjects.map((p, i) => (
                        <motion.div
                          key={p.id}
                          initial={{ opacity: 0, y: 16 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.35, delay: i * 0.04 }}
                          className={`group border p-5 flex flex-col gap-2 hover:-translate-y-1 transition-transform duration-300 ${
                            p.flagship ? "border-auroraDeep bg-auroraDeep/[0.05]" : "border-line"
                          }`}
                          data-testid={`project-${p.id}`}
                        >
                          {p.flagship && (
                            <span className="font-mono-tech text-[9px] uppercase tracking-[0.2em] text-auroraDeep">
                              ★ {e.flagshipLabel}
                            </span>
                          )}
                          <span className="font-display font-medium text-lg leading-snug">{p.name[lang]}</span>
                          <span className="font-mono-tech text-[10px] uppercase tracking-wider text-stone">
                            {p.roleTag[lang]} <span className="text-line">· {p.year}</span>
                          </span>
                          <p className="text-sm font-light text-stone leading-relaxed">{p.description[lang]}</p>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {p.categories.map((c) => (
                              <span
                                key={c}
                                className="font-mono-tech text-[9px] uppercase tracking-wider border border-line/70 text-stone px-2 py-1 transition-colors duration-300 group-hover:border-ink group-hover:text-ink"
                              >
                                {CATEGORY_LABELS[lang][c]}
                              </span>
                            ))}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
